import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  registerables as registerablesJS,
} from 'chart.js';
import CountUp from 'react-countup';
import {
  getDebit,
  getListAnnounceDashboard,
  getListMail,
  getListStock,
  getReceiveslipChart,
  getSummary,
} from 'services/dashboard.service';
import RevenueChart from './components/RevenueChart';
import { DatePicker, Empty, Popover } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import moment from 'moment';
import _ from 'lodash';
import Mail from 'pages/Mail/Mail';
import DebitChart from './components/DebitChart';
import useVerifyAccess from 'hooks/useVerifyAccess';
import styled from 'styled-components';
import { PortalIcon } from 'utils/baochauTheme';
import { getPortalScope, PORTAL_SCOPE_CHANGE_EVENT } from 'utils/portalScope';
import { formatRecognizedRevenueAmount, sumRecognizedRevenueAmounts } from 'utils/recognizedRevenueAmount';
import { getUnresolvedRevenueEventNotice } from 'utils/recognizedRevenueBasis';
// import imageDefault from 'src/public/bw_image/default_avatar_v2.png';
import imageDefault from 'assets/bw_image/logo.png';
const { RangePicker } = DatePicker;
ChartJS.register(...registerablesJS);
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

// export const options = {
//   scales: {
//     y: {
//       beginAtZero: true,
//     },
//   },
//   plugins: {
//     legend: false,
//   },
// };

// const dataChart2 = {
//   labels: ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'],
//   datasets: [
//     {
//       type: 'line',
//       label: 'LNG/DT',
//       data: [15, 10, 20, 15, 21, 16, 18, 19.5, 15, 12, 31, 13],
//       backgroundColor: ['#2D9CDB'],
//       borderColor: ['#2D9CDB'],
//       borderWidth: 2,
//       tension: 0.5,
//     },
//     {
//       type: 'line',
//       label: 'Giá vốn/DT',
//       data: [11, 9, 7, 10, 11, 11, 14, 14, 8.6, 9, 14, 13],
//       backgroundColor: ['#27AE60'],
//       borderColor: ['#27AE60'],
//       borderWidth: 2,
//       tension: 0.5,
//     },
//   ],
// };
const DebitChartStyled = styled.div`
  .bw_debit_chart {
    width: min(100%, 300px);
    aspect-ratio: 1 / 1;
    height: auto;
    margin: 8px auto 4px;
  }

  .bw_debit_chart canvas {
    max-width: 100% !important;
    max-height: 100% !important;
  }

  @media (max-width: 1600px) {
    .bw_debit_chart {
      width: min(100%, 270px);
    }
  }

  @media (max-width: 1366px) {
    .bw_debit_chart {
      width: min(100%, 300px);
    }
  }

  @media (max-width: 1180px) {
    .bw_debit_chart {
      width: min(100%, 340px);
    }
  }
`;

const emptyStockData = {
  list_product: [],
  list_category: [],
};

const DASHBOARD_REVENUE_PERMISSIONS = ['DASHBOARD_REVENUE_VIEW'];
const DASHBOARD_COMMISSION_PERMISSIONS = ['DASHBOARD_COMMISSION_VIEW'];
const DASHBOARD_ORDER_PERMISSIONS = ['DASHBOARD_ORDER_VIEW'];
const DASHBOARD_PURCHASE_INCOMING_PERMISSIONS = ['DASHBOARD_PURCHASE_INCOMING_VIEW'];
const DASHBOARD_STOCK_PERMISSIONS = ['DASHBOARD_STOCK_VIEW'];
const DASHBOARD_DEBIT_PERMISSIONS = ['DASHBOARD_DEBIT_VIEW'];
const DASHBOARD_LEAVE_PERMISSIONS = ['DASHBOARD_LEAVE_VIEW'];
const DASHBOARD_MAIL_PERMISSIONS = ['DASHBOARD_MAIL_VIEW'];
const DASHBOARD_ANNOUNCEMENT_PERMISSIONS = ['DASHBOARD_ANNOUNCEMENT_VIEW'];

const DashboardPage = () => {
  const [scope, setScope] = useState(getPortalScope);
  const [summaryData, setSumaryData] = useState({});
  const [announceList, setAnnounceList] = useState([]);
  const [emailList, setEmailList] = useState([]);
  const [startDate, setStartDate] = useState(() => moment().startOf('month').format('DD/MM/YYYY'));
  const [endDate, setEndDate] = useState(() => moment().endOf('month').format('DD/MM/YYYY'));
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');
  const [revenueError, setRevenueError] = useState('');
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [receiveslipData, setReceiveslipData] = useState([]);
  const [stockData, setStockData] = useState(emptyStockData);
  const [debitData, setDebitData] = useState({});
  const [notesOpen, setNotesOpen] = useState(false);
  const { verifyPermission } = useVerifyAccess();
  const canViewRevenue = verifyPermission(DASHBOARD_REVENUE_PERMISSIONS, true);
  const canViewCommission = verifyPermission(DASHBOARD_COMMISSION_PERMISSIONS, true);
  const canViewOrder = verifyPermission(DASHBOARD_ORDER_PERMISSIONS, true);
  const canViewPurchaseIncoming = verifyPermission(DASHBOARD_PURCHASE_INCOMING_PERMISSIONS, true);
  const canViewStock = verifyPermission(DASHBOARD_STOCK_PERMISSIONS, true);
  const canViewDebit = verifyPermission(DASHBOARD_DEBIT_PERMISSIONS, true);
  const canViewLeave = verifyPermission(DASHBOARD_LEAVE_PERMISSIONS, true);
  const canViewMail = verifyPermission(DASHBOARD_MAIL_PERMISSIONS, true);
  const canViewAnnouncement = verifyPermission(DASHBOARD_ANNOUNCEMENT_PERMISSIONS, true);
  const getTrendDirection = (current, previous) => Math.sign(Number(current || 0) - Number(previous || 0));
  const statusRevenue = getTrendDirection(
    summaryData?.total_revenue_quarter_now,
    summaryData?.total_revenue_quarter_before,
  );
  const statusComission = getTrendDirection(
    summaryData?.total_commission_quarter_now,
    summaryData?.total_commission_quarter_before,
  );
  const statusOrder = getTrendDirection(
    summaryData?.total_order_quarter_now,
    summaryData?.total_order_quarter_before,
  );
  const statusGoods = getTrendDirection(
    summaryData?.total_goods_quarter_now,
    summaryData?.total_goods_quarter_before,
  );
  const recognizedDailyTotal = useMemo(() => sumRecognizedRevenueAmounts(receiveslipData.map((day) =>
    day.net_revenue_exact ?? day.total_money_before_vat_exact ?? day.total_money_before_vat ?? day.total_money)), [receiveslipData]);

  const loadSumaryData = useCallback(() => {
    let disposed = false;
    setSumaryData({});
    setSummaryLoading(true);
    setSummaryError('');
    getSummary({ ...scope, start_date: startDate, end_date: endDate })
      .then((data) => { if (!disposed) setSumaryData(data || {}); })
      .catch(() => { if (!disposed) setSummaryError('Chưa tải được số tổng hợp trong kỳ đã chọn.'); })
      .finally(() => { if (!disposed) setSummaryLoading(false); });
    return () => { disposed = true; };
  }, [endDate, scope, startDate]);

  useEffect(loadSumaryData, [loadSumaryData]);
  useEffect(() => { setNotesOpen(false); }, [startDate, endDate, scope]);

  const loadGetListMail = useCallback(() => {
    if (!canViewMail) {
      setEmailList({ items: [], totalItems: 0 });
      return;
    }
    getListMail()
      .then((e) => setEmailList(e))
      .catch(() => setEmailList({ items: [], totalItems: 0 }))
      .finally(() => { });
  }, [canViewMail]);
  useEffect(loadGetListMail, [loadGetListMail]);

  const loadAnnouceDashboardList = useCallback(() => {
    if (!canViewAnnouncement) {
      setAnnounceList([]);
      return;
    }
    getListAnnounceDashboard()
      .then((e) => setAnnounceList(e?.items?.data || []))
      .catch(() => setAnnounceList([]))
      .finally(() => { });
  }, [canViewAnnouncement]);
  useEffect(loadAnnouceDashboardList, [loadAnnouceDashboardList]);

  const getColor = (value) => (value > 0 ? 'bw_green' : value < 0 ? 'bw_red' : 'bw_blue');
  const getUpDown = (value) => (value > 0 ? '+' : value < 0 ? '-' : '');
  const getTrendIcon = (value) =>
    value > 0 ? 'fi fi-rr-arrow-trend-up' : value < 0 ? 'fi fi-rr-arrow-trend-down' : 'fi fi-rr-minus-small';

  const loadReceiveslipChart = useCallback(() => {
    if (!canViewRevenue) {
      setReceiveslipData([]);
      setRevenueLoading(false);
      return;
    }
    let disposed = false;
    setReceiveslipData([]);
    setRevenueError('');
    setRevenueLoading(true);
    getReceiveslipChart({ ...scope, start_date: startDate, end_date: endDate })
      .then((data) => {
        if (!Array.isArray(data)) throw new Error('Chưa đọc đủ dữ liệu doanh thu.');
        if (!disposed) setReceiveslipData(data);
      })
      .catch(() => { if (!disposed) setRevenueError('Chưa tải được doanh thu trong kỳ đã chọn.'); })
      .finally(() => { if (!disposed) setRevenueLoading(false); });
    return () => { disposed = true; };
  }, [canViewRevenue, endDate, scope, startDate]);

  useEffect(loadReceiveslipChart, [loadReceiveslipChart]);

  const loadStock = useCallback(() => {
    if (!canViewStock) {
      setStockData(emptyStockData);
      return;
    }

    getListStock(scope)
      .then((e) => setStockData(e || emptyStockData))
      .catch(() => setStockData(emptyStockData))
      .finally(() => { });
  }, [canViewStock, scope]);
  useEffect(loadStock, [loadStock]);

  const loadDebit = useCallback(() => {
    if (!canViewDebit) {
      setDebitData({});
      return;
    }

    getDebit(scope)
      .then((e) => setDebitData(e || {}))
      .catch(() => setDebitData({}))
      .finally(() => { });
  }, [canViewDebit, scope]);
  useEffect(loadDebit, [loadDebit]);


  useEffect(() => {
    const handler = (event) => setScope(event?.detail || getPortalScope());
    window.addEventListener(PORTAL_SCOPE_CHANGE_EVENT, handler);
    return () => window.removeEventListener(PORTAL_SCOPE_CHANGE_EVENT, handler);
  }, []);

  const renderSummaryNumber = (key, props = {}) => summaryError ? 'Chưa tải được'
    : summaryLoading ? 'Đang tải...' : key === 'total_revenue_quarter_now'
      ? formatRecognizedRevenueAmount(summaryData.recognized_revenue?.summary?.net_revenue_exact ?? summaryData[key])
      : <CountUp end={summaryData?.[key]} separator='.' {...props} />;
  const comparisonFrom = summaryData?.comparison_date_from;
  const comparisonTo = summaryData?.comparison_date_to;

  return (
    <div className='bw_main_wrapp bw_dashboard_page'>
      <div className='bw_card_items bw_dashboard_panel bw_dashboard_period_panel bw_mb_2' aria-label='Kỳ tổng quan'>
        <div className='bw_row bw_align_items_center bw_dashboard_panel_header'>
          <div className='bw_col_7 bw_dashboard_period_title'>
            <h2 className='bw_title_card'>Tổng quan từ {startDate} đến {endDate}</h2>
            <Popover trigger='click' placement='bottomLeft' open={notesOpen} onOpenChange={setNotesOpen}
              content={<div className='bw_dashboard_period_notes'>
                <p>Doanh thu thuần chưa VAT. Hàng hóa và số đơn theo ngày thực xuất kho; dịch vụ không cần xuất kho theo ngày hạch toán đã ghi sổ; trả hàng theo ngày nhập trả thực tế. Đã trừ các khoản giảm trừ doanh thu.</p>
                {canViewRevenue && getUnresolvedRevenueEventNotice(summaryData.recognized_revenue?.meta) ? <p>{getUnresolvedRevenueEventNotice(summaryData.recognized_revenue.meta)}</p> : null}
                {comparisonFrom && comparisonTo ? <p>So sánh với {moment(comparisonFrom).format('DD/MM/YYYY')} – {moment(comparisonTo).format('DD/MM/YYYY')}.</p> : null}
              </div>}>
              <button type='button' className='bw_dashboard_info_button' aria-label='Thông tin kỳ và cách tính Dashboard' aria-expanded={notesOpen}>
                <InfoCircleOutlined aria-hidden='true' />
              </button>
            </Popover>
          </div>
          <div className='bw_col_5'>
            <RangePicker
              format='DD/MM/YYYY'
              placeholder={['Từ ngày', 'Đến ngày']}
              className='bw_dashboard_range_picker'
              allowClear={false}
              value={[dayjs(moment(startDate, 'DD/MM/YYYY', true).toDate()), dayjs(moment(endDate, 'DD/MM/YYYY', true).toDate())]}
              onChange={(dates, dateStrings) => {
                if (dates) { setStartDate(dateStrings[0]); setEndDate(dateStrings[1]); }
              }}
            />
          </div>
        </div>
        {summaryError ? <p role='alert'>{summaryError}</p> : null}
      </div>
      <div className='bw_row bw_dashboard_kpi_row'>
        {canViewRevenue && (
            <div className='bw_c2 bw_dashboard_kpi_col'>
              <div className='bw_card_items bw_dashboard_kpi_card'>
                <span className='bw_icon_card bw_orange'>
                  <PortalIcon icon='fi fi-rr-dollar' />
                </span>
                <p>Doanh thu thuần (chưa VAT)</p>
                <h4>
                  {renderSummaryNumber('total_revenue_quarter_now')}
                </h4>
                {!summaryLoading && !summaryError && (<span className={`bw_trans ${getColor(statusRevenue)} `}>
                  <PortalIcon icon={getTrendIcon(statusRevenue)} style={{ marginRight: '4px', lineHeight: 0 }} />
                  {getUpDown(statusRevenue)}{' '}
                  <CountUp end={summaryData?.revenue_quarter_percent} />%
                </span>)}
              </div>
            </div>
        )}
        {canViewCommission && (
            <div className='bw_c2 bw_dashboard_kpi_col'>
              <div className='bw_card_items bw_dashboard_kpi_card'>
                <span className='bw_icon_card bw_orange'>
                  <PortalIcon icon='fi fi-rr-dollar' />
                </span>
                <p>Hoa hồng đã ghi sổ (đ)</p>
                <h4>
                  {renderSummaryNumber('total_commission_quarter_now')}
                </h4>
                <div className='bw_dashboard_commission_breakdown'>
                  <span>
                    Bán hàng:{' '}
                    <b>{renderSummaryNumber('total_sales_commission_quarter_now', { separator: ',' })}đ</b>
                  </span>
                  <span>
                    Giao hàng:{' '}
                    <b>{renderSummaryNumber('total_delivery_commission_quarter_now', { separator: ',' })}đ</b>
                  </span>
                </div>
                {!summaryLoading && !summaryError && Number(summaryData?.effective_sales_commission_policy_count || 0) === 0 && (
                  <div className='bw_dashboard_commission_warning'>
                    Chưa có chính sách hoa hồng bán hàng hiệu lực
                  </div>
                )}
                {!summaryLoading && !summaryError && (<span className={`bw_trans ${getColor(statusComission)}`}>
                  <PortalIcon icon={getTrendIcon(statusComission)} style={{ marginRight: '4px', lineHeight: 0 }} />
                  {getUpDown(statusComission)}{' '}
                  <CountUp end={summaryData?.comission_quarter_percent} />%
                </span>)}
              </div>
            </div>
        )}
        {canViewOrder && (
            <div className='bw_c2 bw_dashboard_kpi_col'>
              <div className='bw_card_items bw_dashboard_kpi_card'>
                <span className='bw_icon_card bw_red'>
                  <PortalIcon icon='fi fi-rr-shopping-cart' />
                </span>
                <p>Đơn đã ghi nhận doanh thu</p>
                <h4>
                  {renderSummaryNumber('total_order_quarter_now')}
                </h4>
                {!summaryLoading && !summaryError && (<span className={`bw_trans ${getColor(statusOrder)}`}>
                  <PortalIcon icon={getTrendIcon(statusOrder)} style={{ marginRight: '4px', lineHeight: 0 }} />
                  {getUpDown(statusOrder)}{' '}
                  <CountUp end={summaryData?.order_quarter_percent} />%
                </span>)}
              </div>
            </div>
        )}
        {canViewPurchaseIncoming && (
          <div className='bw_c2 bw_dashboard_kpi_col'>
            <div className='bw_card_items bw_dashboard_kpi_card'>
              <span className='bw_icon_card '>
                <PortalIcon icon='fi fi-rr-package' />
              </span>
              <p>Hàng đặt chưa về</p>
              <h4>
                {renderSummaryNumber('total_goods_quarter_now')}
              </h4>
              {!summaryLoading && !summaryError && (<span className={`bw_trans ${getColor(statusGoods)}`}>
                <PortalIcon icon={getTrendIcon(statusGoods)} style={{ marginRight: '4px', lineHeight: 0 }} />
                {getUpDown(statusGoods)}{' '}
                <CountUp end={summaryData?.goods_quarter_percent} />%
              </span>)}
            </div>
          </div>
        )}
        {canViewLeave && (
          <div className='bw_c2 bw_dashboard_kpi_col'>
            <div className='bw_card_items bw_dashboard_kpi_card'>
              <span className='bw_icon_card bw_blue'>
                <PortalIcon icon='fi fi-rr-calendar' />
              </span>
              <p>Phép còn (ngày)</p>
              <h4>{summaryData?.total_off_work}/12</h4>
              <span onClick={() => window._$g.rdr(`/off-work`)}>Xem thêm</span>
            </div>
          </div>
        )}
      </div>
      <div className='bw_row bw_dashboard_content_row'>
        {canViewRevenue && (
          <div className='bw_col_8 bw_dashboard_main_col'>
            <div className='bw_card_items bw_dashboard_panel bw_dashboard_revenue_panel'>
              <div className='bw_row bw_align_items_center bw_dashboard_panel_header'>
                <div className='bw_col_7'>
                  <h2 className='bw_title_card'>Doanh thu thuần (chưa VAT)</h2>
                </div>

              </div>
              {revenueError ? <p role='alert'>{revenueError}</p> : null}
              <div className='bw_img_chart bw_dashboard_chart_box'>
                <div className='bw_number_tu bw_dashboard_revenue_summary'>
                  <div className='bw_dashboard_revenue_metric'>
                    <span>Doanh thu thuần (chưa VAT)</span>
                    <b>
                      {revenueError ? 'Chưa tải được' : revenueLoading ? 'Đang tải...' : `${formatRecognizedRevenueAmount(recognizedDailyTotal)}đ`}
                    </b>
                  </div>

                </div>
                <div className='bw_dashboard_chart_canvas'>
                  <RevenueChart data={receiveslipData} />
                </div>
              </div>
            </div>
          </div>
        )}
        {canViewDebit && (
          <div className='bw_col_4 bw_dashboard_side_col'>
            <div className='bw_card_items bw_dashboard_panel bw_dashboard_debit_panel'>
              <h2 className='bw_title_card'>
                <PortalIcon icon='fi fi-rr-database' style={{ marginRight: '8px', lineHeight: 0 }} /> Công nợ hiện tại
              </h2>
              <DebitChartStyled>
                <div className='bw_border_top bw_box_list_card'>
                  <div className='bw_debit_chart'>
                    <DebitChart data={debitData} />
                  </div>
                  <a href='/debit'>Xem chi tiết</a>
                </div>
              </DebitChartStyled>
            </div>
          </div>
        )}
        {/* <div className='bw_col_6 bw_mt_2'>
          <div className='bw_card_items'>
            <div className='bw_row bw_align_items_center'>
              <div className='bw_col_7'>
                <h2 className='bw_title_card'>Lợi nhuận</h2>
              </div>
              <div className='bw_col_5'>
                <div className='bw_show_date' id='bw_choose_calendar'>
                  <span className='fi fi-rr-calendar'></span> 01/10/2022 - 30/10/2022
                </div>
                <div className='bw_hide bw_choose_inp'>
                  <div className='bw_show_calendar'>
                    <input type='text' id='calendar2' data-mode='range' />
                  </div>
                  <a className='bw_apply' id='bw_choose_date'>
                    Áp dụng
                  </a>
                </div>
              </div>
            </div>
            <div className='bw_img_chart bw_mt_1'>
              <div className='bw_number_tu bw_text_center'>
                Tổng lợi nhuận: <b>20.000.000 đ</b>
              </div>
              <Chart options={options} data={dataChart2} />
            </div>
          </div>
        </div> */}
        {canViewStock && (
          <>
            <div className='bw_col_6 bw_dashboard_half_col'>
              <div className='bw_card_items bw_dashboard_panel'>
                <h2 className='bw_title_card'>
                  <PortalIcon icon='fi fi-rr-package' style={{ marginRight: '8px', lineHeight: 0 }} /> Tồn kho
                </h2>
                <div className='bw_border_top bw_box_list_card'>
                  <div className='bw_title_list_card'>
                    <b>Ngành hàng</b>
                    <b>Số lượng tồn</b>
                  </div>
                  <ul>
                    {' '}
                    {stockData?.list_category?.map((x, index) => (
                      <li key={index}>
                        <span>{x.category_name}</span>
                        <b>{x.total}</b>
                      </li>
                    ))}
                  </ul>
                  <a href='/stocks-detail'>Xem chi tiết</a>
                </div>
              </div>
            </div>
            <div className='bw_col_6 bw_dashboard_half_col'>
              <div className='bw_card_items bw_dashboard_panel'>
                <h2 className='bw_title_card'>
                  <PortalIcon icon='fi fi-rr-boxes' style={{ marginRight: '8px', lineHeight: 0 }} /> Sản phẩm tồn kho
                </h2>
                <div className='bw_border_top bw_box_list_card'>
                  <div className='bw_title_list_card'>
                    <b>Sản phẩm</b>
                    <b>Số lượng tồn</b>
                  </div>
                  <ul>
                    {stockData?.list_product?.map((x, i) => (
                      <li key={i}>
                        <span>{x.product_name}</span>
                        <b>{x.total}</b>
                      </li>
                    ))}
                  </ul>
                  <a href='/stocks-detail'>Xem chi tiết</a>
                </div>
              </div>
            </div>
          </>
        )}
        {canViewMail && (
          <div className='bw_col_6 bw_dashboard_half_col'>
          <div className='bw_card_items bw_dashboard_panel'>
            <h2 className='bw_title_card'>
              <PortalIcon icon='fi fi-rr-envelope-open-text' style={{ marginRight: '8px', lineHeight: 0 }} /> Hộp thư{' '}
              <i>({emailList?.totalItems})</i>
            </h2>
            <div className='bw_border_top bw_dashboard_embedded_table'>
              <Mail hiddenMenu noPaging />
            </div>
            {Boolean(emailList?.totalItems) && (
              <div className='bw_footer_card'>
                <a href='/mail' className='bw_green'>
                  Xem tất cả
                </a>
                <PortalIcon icon='fi fi-rr-angle-small-right' />
              </div>
            )}
          </div>
          </div>
        )}
        {canViewAnnouncement && (
          <div className='bw_col_6 bw_dashboard_half_col'>
          <div className='bw_card_items bw_dashboard_panel'>
            <h2 className='bw_title_card'>
              <PortalIcon icon='fi fi-rr-bell' style={{ marginRight: '8px', lineHeight: 0 }} /> Thông báo nội bộ
            </h2>
            <div>
              <div className='bw_border_top bw_dashboard_notice_list'>
                {_.size(announceList) ? (
                  (announceList ?? [])?.map((o, index) => {
                    return (
                      <a
                        key={index}
                        href={`/notification/detail/${o?.announce_id}`}
                        className='bw_list_email bw_list_notice'>
                        <div className='bw_us_email bw_us_notice'>
                          <img alt='' src={o?.user_img_url}
                            onError={(e) => {
                              e.target.src = imageDefault
                            }}
                          />
                          <h3>{o?.user_fullname}</h3>
                        </div>
                        <p>{o?.announce_title} </p>
                        <span className='bw_has_file'>
                          <PortalIcon icon='fi fi-rr-clip' />
                        </span>
                        <span className='bw_time_email'>{moment(o?.news_date).startOf('hour').fromNow()}</span>
                      </a>
                    );
                  })
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className='bw_mt_5' description='Không có dữ liệu' />
                )}
              </div>
              {Boolean(_.size(announceList)) && (
                <div className='bw_footer_card'>
                  <a href='/announce-view/' className='bw_green'>
                    Xem tất cả
                  </a>
                  <PortalIcon icon='fi fi-rr-angle-small-right' />
                </div>
              )}
            </div>
          </div>
          </div>
        )}
        {/* <div className='bw_col_12 bw_mt_2'>
          <div className='bw_card_items'>
            <h2 className='bw_title_card'>
              <span className='fi fi-rr-clock-two'></span> Lịch sử hoạt động
            </h2>
            <div className='bw_mt_2 bw_border_top'>
              <div className='bw_table_responsive'>
                <table className='bw_table'>
                  <thead>
                    <th className='bw_sticky bw_check_sticky'>STT</th>
                    <th>Thời gian</th>
                    <th>Phiếu yêu cầu</th>
                    <th>Nội dung</th>
                    <th className='bw_sticky bw_action_table bw_text_center'>Thao tác</th>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='bw_sticky bw_check_sticky'>1</td>
                      <td>21/12/2021 12:00</td>
                      <td>#PXNB22092700001</td>
                      <td>
                        <p>
                          <b>Chuyển trạng thái</b>
                        </p>
                        10020 - Hoàng Văn Minh đã chuyển task từ <span className='bw_black'>Tiếp nhận</span> -{' '}
                        <span className='bw_blue'>Đang xử lý</span>
                      </td>
                      <td className='bw_text_center'>
                        <a href='index.php?page=641' className='bw_green'>
                          <i className='fi fi-rr-plus'></i>
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td className='bw_sticky bw_check_sticky'>1</td>
                      <td>21/12/2021 12:00</td>
                      <td>#PXNB22092700323</td>
                      <td>
                        <p>
                          <b>Chuyển trạng thái</b>
                        </p>
                        10020 - Hoàng Văn Minh đã chuyển task từ <span className='bw_black'>Tiếp nhận</span> -{' '}
                        <span className='bw_blue'>Đang xử lý</span>
                      </td>
                      <td className='bw_text_center'>
                        <a href='index.php?page=641' className='bw_green'>
                          <i className='fi fi-rr-plus'></i>
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className='bw_footer_card'>
              <a href='index.php?page=54' className='bw_green'>
                Xem tất cả
              </a>
              <span className='fi fi-rr-angle-double-small-right'></span>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default DashboardPage;
